import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Typography,
	Stack,
	Divider,
	CircularProgress,
	useTheme,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SectionHeader from './SectionHeader';
import { makeApiRequest } from '../../../../Shared/Api';
import { useRealtimeEvent } from '../../../../Context/RealtimeContext';
import { surfaceCard } from '../../../../Shared/surfaceCard';

const DOCUMENT_LABELS = {
	BID: 'Proposal',
	INVOICE: 'Invoice',
	INVOICE_REMINDER: 'Invoice',
	REFUND: 'Refund notification',
};

const sortEvents = (events) =>
	[...events].sort(
		(a, b) =>
			new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
	);

const formatActivityEventDescription = (event) => {
	const label = DOCUMENT_LABELS[event.document_type] || 'Email';

	switch (event.event_type) {
		case 'opened':
			return `${label} email opened`;
		case 'sent': {
			const recipient = event.recipient_email || 'customer';
			return `${label} sent to ${recipient}`;
		}
		case 'bid_accepted':
			return 'Proposal accepted';
		case 'acceptance_revoked':
			return 'Acceptance cancelled';
		case 'bid_declined':
			return 'Proposal declined';
		case 'expiration_reminder_sent': {
			const recipient = event.recipient_email || 'customer';
			return `Reminder sent to ${recipient}`;
		}
		case 'invoice_reminder_sent': {
			const recipient = event.recipient_email || 'customer';
			return `Invoice reminder sent to ${recipient}`;
		}
		case 'deposit_received':
			return 'Deposit payment received';
		case 'payment_received':
			return 'Invoice payment received';
		case 'refund_issued':
			return 'Refund issued';
		case 'invoice_voided':
			return 'Invoice voided';
		default:
			return 'Activity recorded';
	}
};

const getEventIcon = (eventType) => {
	switch (eventType) {
		case 'opened':
			return DraftsOutlinedIcon;
		case 'bid_accepted':
			return CheckCircleOutlineIcon;
		case 'acceptance_revoked':
			return HistoryIcon;
		case 'bid_declined':
			return CancelOutlinedIcon;
		case 'expiration_reminder_sent':
		case 'invoice_reminder_sent':
			return NotificationsActiveOutlinedIcon;
		case 'deposit_received':
		case 'payment_received':
			return CheckCircleOutlineIcon;
		case 'refund_issued':
			return NotificationsActiveOutlinedIcon;
		case 'invoice_voided':
			return CancelOutlinedIcon;
		case 'sent':
		default:
			return MailOutlineIcon;
	}
};

const getEventColor = (eventType, theme) => {
	switch (eventType) {
		case 'opened':
		case 'bid_accepted':
		case 'deposit_received':
		case 'payment_received':
			return 'success.main';
		case 'bid_declined':
		case 'invoice_voided':
			return 'error.main';
		case 'acceptance_revoked':
			return 'warning.main';
		case 'refund_issued':
			return 'warning.main';
		default:
			return theme.palette.text.secondary;
	}
};

const JobEmailEventsPanel = ({ jobId, refreshKey = 0 }) => {
	const theme = useTheme();
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!jobId) return undefined;

		let cancelled = false;
		setLoading(true);

		makeApiRequest('GET', `/jobs/${jobId}/email-events/`)
			.then((data) => {
				if (!cancelled) {
					setEvents(sortEvents(Array.isArray(data) ? data : []));
				}
			})
			.catch(() => {
				if (!cancelled) {
					setEvents([]);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [jobId, refreshKey]);

	const handleRealtimeUpdate = useCallback(
		(message) => {
			if (message?.type !== 'job.email_event') {
				return;
			}
			if (String(message.job_id) !== String(jobId)) {
				return;
			}
			const incoming = message.email_event;
			if (!incoming?.id) {
				return;
			}
			setEvents((prev) => {
				if (prev.some((event) => event.id === incoming.id)) {
					return prev;
				}
				return sortEvents([incoming, ...prev]);
			});
		},
		[jobId],
	);

	useRealtimeEvent(handleRealtimeUpdate);

	return (
		<Box
			sx={{
				...surfaceCard(theme),
				display: 'flex',
				flexDirection: 'column',
				maxHeight: 320,
				minHeight: 0,
			}}
		>
			<SectionHeader icon={HistoryIcon} title="Activity" />
			<Divider sx={{ opacity: 0.6 }} />
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
				}}
			>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
						<CircularProgress size={22} />
					</Box>
				) : events.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No activity for this job yet.
					</Typography>
				) : (
					<Stack spacing={0}>
						{events.map((event, index) => {
							const Icon = getEventIcon(event.event_type);
							return (
								<Box
									key={event.id}
									sx={{
										display: 'flex',
										gap: 1.25,
										py: 1.25,
										borderBottom:
											index === events.length - 1 ? 'none' : '1px solid',
										borderColor: 'divider',
									}}
								>
									<Box
										sx={{
											mt: 0.25,
											color: getEventColor(event.event_type, theme),
											display: 'flex',
										}}
									>
										<Icon sx={{ fontSize: 18 }} />
									</Box>
									<Box sx={{ minWidth: 0 }}>
										<Typography variant="body2" fontWeight={500}>
											{formatActivityEventDescription(event)}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											sx={{ mt: 0.25 }}
										>
											{new Date(event.occurred_at).toLocaleString(undefined, {
												dateStyle: 'medium',
												timeStyle: 'short',
											})}
										</Typography>
									</Box>
								</Box>
							);
						})}
					</Stack>
				)}
			</Box>
		</Box>
	);
};

export default JobEmailEventsPanel;
